import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Tag, InputNumber, Switch, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const MimeManager = ({ vhostName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (vhostName) loadData();
    }, [vhostName]);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getMime(vhostName);
            if (response.success) {
                const items = Array.isArray(response.data) ? response.data : [];
                setDataSource(
                    items.map((item, index) => ({
                        key: item.ext || item.name || index,
                        ext: item.ext || item.name || '',
                        type: item.type || '',
                        cache: Number(item.cache || 0),
                        gzip: Boolean(item.gzip)
                    }))
                );
            } else {
                message.error(response.error || t('Failed to load MIME types'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Failed to load: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            const payload = {
                ext: (values.ext || '').replace(/^\./, '').trim(),
                type: (values.type || '').trim(),
                cache: Number(values.cache || 0),
                gzip: values.gzip ? 1 : 0
            };

            const response = await hostSharedService.addMime(vhostName, payload);
            if (response.success) {
                message.success(t('Added successfully'));
                setModalVisible(false);
                loadData();
            } else {
                message.error(response.error || t('Failed to add'));
            }
        } catch (error) {
            if (!error.errorFields) message.error(t('Failed to add: {{message}}', { message: error.response?.data?.error || error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (ext) => {
        setLoading(true);
        try {
            const response = await hostSharedService.deleteMime(vhostName, ext);
            if (response.success) {
                message.success(t('Deleted successfully'));
                loadData();
            } else {
                message.error(response.error || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed: {{message}}', { message: error.response?.data?.error || error.message }));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { title: t('Extension name'), dataIndex: 'ext', key: 'ext', render: (text) => <Tag color="blue">{text || '-'}</Tag> },
        { title: t('MIME type'), dataIndex: 'type', key: 'type' },
        {
            title: t('Cache TTL (seconds)'),
            dataIndex: 'cache',
            key: 'cache',
            render: (value) => (value ? `${value}` : '0')
        },
        {
            title: t('Enable compression'),
            dataIndex: 'gzip',
            key: 'gzip',
            render: (value) => <Tag color={value ? 'green' : 'default'}>{value ? t('Yes') : t('No')}</Tag>
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Popconfirm title={t('Are you sure you want to delete?')} onConfirm={() => handleDelete(record.ext)} okText={t('OK')} cancelText={t('Cancel')}>
                    <Button type="link" danger size="small" icon={<DeleteOutlined />}>{t('Delete')}</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        form.resetFields();
                        form.setFieldsValue({ cache: 3600, gzip: true });
                        setModalVisible(true);
                    }}
                >
                    {t('Add MIME type')}
                </Button>
            </Space>
            <Table rowKey="ext" columns={columns} dataSource={dataSource} loading={loading} pagination={{ pageSize: 10 }} />
            <Modal title={t('Add MIME type')} open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} confirmLoading={loading}>
                <Form form={form} layout="vertical" initialValues={{ cache: 3600, gzip: true }}>
                    <Alert
                        message={t('MIME type guide')}
                        description={t('Example: extension webp, type image/webp, cache duration 3600 seconds. When compression is enabled, matching file types will be compressed with Gzip.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        icon={<FileTextOutlined />}
                    />
                    <Form.Item label={t('File extension')} name="ext" rules={[{ required: true, message: t('Please enter a file extension') }, { pattern: /^\.?[A-Za-z0-9_-]{1,12}$/, message: t('Invalid file extension') }]}>
                        <Input placeholder={t('E.g. webp')} />
                    </Form.Item>
                    <Form.Item label={t('MIME type')} name="type" rules={[{ required: true, message: t('Please enter a MIME type') }, { pattern: /^[A-Za-z0-9][\w!#$&^.+-]*\/[A-Za-z0-9][\w!#$&^.+-]*$/, max: 64, message: t('Invalid MIME type') }]}>
                        <Input placeholder={t('E.g. image/webp')} />
                    </Form.Item>
                    <Form.Item label={t('Cache TTL (seconds)')} name="cache" rules={[{ required: true, message: t('Enter the cache duration') }]}>
                        <InputNumber min={0} style={{ width: '100%' }} placeholder={t('E.g. 3600')} />
                    </Form.Item>
                    <Form.Item label={t('Enable compression')} name="gzip" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MimeManager;
